import React, { useEffect, useState } from 'react';

import GeneratePoCard from './generatePoCard';
import { CircularProgress, Grid, Typography, Modal, Box, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  DescriptionOutlined,
  ShoppingCart,
  Check,
  ErrorOutline,
  SwitchAccountOutlined
} from '@mui/icons-material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

import { getSupportingDocument } from '../services/services';
import { Colors, isLightTheme } from '../config/default';
import Navigation from './navigation';
import { autoCap } from '../services/validationService';
import PaginationButtons from './paginationButtons';
import { formatAndFloatNumber } from '../services/common';
import SupportingDocumentWithBoundingBoxes from './SupportingDocumentBoundingBoxes';
import { supportingDocumentsPopup, erpTypes } from '../constants/appConstants';
import { formatLineItems } from '../services/common';
import PoLineitems from './poLineitems';
import EditAttachedTicket from './editAttachedTicket';

function SupportingDocument({
  refId,
  po,
  annotationType,
  deliveryTicketUpload,
  poMatchPermission,
  lineItem,
  lineItemsData,
  onOpenDocument,
  // Rendered on the DT side, beside the attached-ticket controls: the top bar's
  // "Attach delivery ticket" (it opens the same AttachDeliveryTicket the
  // paperclip did). Absent wherever the paperclip was not offered.
  attachDeliveryTicket
}) {
  const {
    NO_DOCUMENTS,
    INVOICE_LINEITEMS,
    NO_LINEITEMS,
    DESCRIPTION,
    QUANTITY,
    NO_PO,
    STATUS,
    SHOW_LINEITEMS,
    SHOW_INVOICE,
    INVOICE,
    PO_TAB,
    COMPARE_PO,
    COMPARE_DT,
    SUPPORTING_DOCUMENT,
    PO_CARD_TITLE
  } = supportingDocumentsPopup;
  const smallScreen = useMediaQuery('(max-height:1400px)');
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const companyId = userDetails?.company?._id;
  const isVista = userDetails?.user?.erpType === erpTypes.VIEWPOINT_VISTA_ERP;
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(false);
  const [images, setImages] = useState([]);
  const { id } = useParams();
  const [selectedItemIndex, setSelectedItemIndex] = useState(null); // New state to track selected item
  const [poStatus, setPoStatus] = useState('');
  const handleImageClick = () => {
    setSelectedImage(!selectedImage);
  };
  const findValue =
    annotationType === 'G702' ||
    ((annotationType === 'Invoice' || annotationType === 'Receipt') &&
      deliveryTicketUpload &&
      !isEmpty(refId))
      ? 1
      : annotationType === 'Invoice' || annotationType === 'Receipt'
      ? deliveryTicketUpload && poMatchPermission
        ? 0
        : poMatchPermission
        ? 0
        : 1
      : 0;
  const [navigationValue, setNavigationValue] = useState(findValue);
  const [index, setIndex] = useState(0);
  const [dtIndex, setDtIndex] = useState(0);
  const [documentContent, setDocumentContent] = useState([]);
  const [documentLineItems, setDocumentLineItems] = useState([]);
  const [response, setResponse] = useState({});
  const [cardOpen, setCardOpen] = useState(false);
  const [dTUrl, setDTUrl] = useState([]);
  const [imageDimensions, setImageDimensions] = useState({});
  const [supportindDocLineitems, setSupportingDocLineItems] = useState([]);
  const [commitmentPosition, setCommitmentPosition] = useState(null);
  const [dtSwitch, setDtSwitch] = useState('lineItems');

  const getDocument = async () => {
    setLoading(true);
    let lineItemsCopy = [...lineItem];
    Object.keys(lineItemsData)?.forEach((key) => {
      if (key !== undefined && lineItemsData[key] !== undefined) {
        lineItemsData[key]?.forEach((val, index) => {
          if (val !== undefined && lineItemsCopy[index] !== undefined) {
            const check = lineItemsCopy[index]?.findIndex((item) => item?.name === key);
            if (check !== -1) {
              lineItemsCopy[index][check].value = val === 'N/A' ? '' : val;
            }
          }
        });
      }
    });
    const lineItems = formatLineItems(lineItemsCopy);
    const type = navigationValue === 1 ? 'DeliveryTicket' : 'Po';
    const findRef = !isEmpty(refId) ? refId[0]?.id : 'N/A';
    const recordNumber = navigationValue === 1 ? findRef : po;
    const initalizedValue = recordNumber === '' ? 'N/A' : recordNumber;
    const params = {
      documentType: type,
      companyId: companyId,
      recordNumber: initalizedValue,
      lineItems: lineItems
    };
    const result = await getSupportingDocument(id, params);

    if (result?.status === 200) {
      setResponse(result?.data?.payload?.data);
      setDocumentContent(result?.data?.payload?.data?.supportingDocument?.standardContent);
      setDocumentLineItems(result?.data?.payload?.data?.supportingDocument?.standardizeLineItems);
      setImages(result?.data?.payload?.data?.document?.convertedS3Keys);
      setDTUrl(result?.data?.payload?.data?.supportingDocument?.allSignedUrl);
      setSupportingDocLineItems(result?.data?.payload?.data?.supportingDocument?.lineItems);
      setPoStatus(result?.data?.payload?.data?.supportingDocument?.documentStatus || '');
      // What is actually left on the commitment. The per-line Received/Remaining
      // columns below are blanked for Foundation — its ERP reports no
      // billed-to-date per line, and the figures that used to sit there were
      // hard-coded copies of the ordered amount — so this is where the real
      // answer lives. Null for every other ERP, where the panel renders nothing.
      setCommitmentPosition(result?.data?.payload?.data?.commitmentPosition ?? null);
    } else {
      setResponse({});
      setCommitmentPosition(null);
    }

    setLoading(false);
  };
  useEffect(() => {
    setDtIndex(0);
    setIndex(0);
    getDocument();
  }, [navigationValue]);
  const slideRight = () => {
    setIndex((index + 1) % images.length);
  };
  const slideLeft = () => {
    const nextIndex = index - 1;
    if (nextIndex < 0) {
      setIndex(images.length - 1);
    } else {
      setIndex(nextIndex);
    }
  };
  const documentContentMapped = documentContent
    ?.map((item) => {
      if (item?.name === 'Estimate_no') {
        return {
          name: 'Order no',
          value: item?.value
        };
      } else if (item?.name === 'Estimate_date') {
        return {
          name: 'Date',
          value: item?.value
        };
      } else if (item?.name === 'Expiration_date') {
        return null;
      } else if (item?.name === 'purchase_order') {
        // The fetched commitment can be a subcontract — the number arrives in the
        // purchase_order cell either way, so the row is labelled for both.
        return {
          name: PO_CARD_TITLE,
          value: item?.value
        };
      } else if (item?.name === 'tax') {
        return {
          name: 'Tax %',
          value: item?.value
        };
      } else if (item?.name === 'total') {
        return {
          name: 'Subtotal',
          value: formatAndFloatNumber(String(item?.value ?? '').replace(/,/g, ''))
        };
      } else {
        return {
          name: autoCap(item?.name),
          value: item?.value
        };
      }
    })
    .filter((item) => item !== null);

  const documentLineItemsMapped = documentLineItems?.map((item) => ({
    ...item,
    qty: formatAndFloatNumber(item?.qty),
    total: formatAndFloatNumber(item?.total),
    unit_price: formatAndFloatNumber(item?.unit_price),
    // Vista PO card columns sourced directly from the API line item
    subtotal: formatAndFloatNumber(item?.subTotal),
    taxRate: item?.tax,
    taxAmount: formatAndFloatNumber(item?.taxAmount)
  }));

  const lineItemsPerPage = 7;
  const remainingLineitems = documentLineItemsMapped?.length - lineItemsPerPage;
  const totalCardPages = Math?.ceil(remainingLineitems / lineItemsPerPage);
  const filteredArray = documentContentMapped?.filter((item) => item?.value !== '');

  const handleCardOpen = () => {
    setCardOpen(true);
  };
  const handleClose = () => {
    setCardOpen(false);
  };
  const slideRightDT = () => {
    setDtIndex((dtIndex + 1) % dTUrl.length);
  };
  const slideLeftDT = () => {
    const nextIndex = dtIndex - 1;
    if (nextIndex < 0) {
      setDtIndex(dTUrl?.length - 1);
    } else {
      setDtIndex(nextIndex);
    }
  };
  const divStyle = {
    margin: '0 10px',
    color: Colors.WHITE,
    display: 'inline-flex',
    fontSize: '12px'
  };

  const isValidPage = (page) => {
    return page !== null && page !== undefined && page !== '' && page !== '-';
  };
  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
  };
  const groupLineItems = (lineItems = []) => {
    const grouped = {};

    lineItems?.forEach((pair) => {
      const desc = pair.find((p) => p.name === 'Description');
      const qty = pair.find((p) => p.name === 'Quantity');

      const description = desc?.value?.trim();
      const quantity = parseInt(qty?.value || '0', 10);

      if (!description) return;

      if (!grouped[description]) {
        grouped[description] = {
          description,
          totalQuantity: 0,
          children: []
        };
      }

      grouped[description].totalQuantity += quantity;
      grouped[description].children.push(pair);
    });

    return Object.values(grouped);
  };

  const groupedItems = groupLineItems(supportindDocLineitems);

  const dtSwitchHandler = () => {
    if (dtSwitch === 'lineItems') {
      setDtSwitch('invoice');
    } else {
      setDtSwitch('lineItems');
    }
  };
  return (
    <Grid
      container
      sx={{
        padding: '1rem',
        height: { xs: '87vh', xl: '85vh' },
        overflowY: { xs: 'auto', lg: 'hidden' }
      }}>
      {loading ? (
        <Grid container sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={45} sx={{ color: Colors.DARKBLUE }} />
        </Grid>
      ) : (
        <>
          <Grid
            item
            xs={12}
            sx={{
              mt: '.5rem',
              marginBottom: '1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItem: 'center'
            }}>
            <Typography variant="h6" sx={{ color: Colors.WHITE }}>
              {navigationValue === 0
                ? COMPARE_PO
                : navigationValue === 1
                ? COMPARE_DT
                : SUPPORTING_DOCUMENT}
            </Typography>
            {/* Status Pill at Top — a synced subcontract carries no status, so the
                pill is dropped rather than shown empty. */}
            {navigationValue === 0 && !isEmpty(response) && poStatus && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
                  borderRadius: '20px',
                  padding: '0.4rem 1rem',
                  width: 'fit-content',
                  gap: '0.5rem',
                  height: '2rem'
                }}>
                <Typography sx={{ fontWeight: 500, color: Colors.WHITE, fontSize: '.8em' }}>
                  {STATUS}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: Colors.TOURQUISE,
                    fontSize: '.8em',
                    border: `1px solid ${Colors.TOURQUISE}`,
                    paddingX: 1,
                    paddingY: '0.2em',
                    borderRadius: '50px'
                  }}>
                  {poStatus}
                </Typography>
              </Box>
            )}
            {/* The attached delivery ticket(s) can be pulled back from here to
                be corrected — the documents list shows them as Attached and
                offers no pull back for them. */}
            {navigationValue === 1 && !isEmpty(refId) && (
              <EditAttachedTicket tickets={refId} onOpenDocument={onOpenDocument} />
            )}
            {navigationValue === 1 && attachDeliveryTicket}
            {(annotationType === 'Invoice' || annotationType === 'Receipt') &&
              deliveryTicketUpload &&
              poMatchPermission && (
                <Navigation
                  value={navigationValue}
                  setValue={setNavigationValue}
                  label1={PO_TAB}
                  label2="DT"
                />
              )}
          </Grid>
          {isEmpty(response) ? (
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: Colors.TEXTGREY
              }}>
              <ErrorOutline
                sx={{ color: Colors.TEXTGREY, marginRight: '.3em', fontSize: '1.1em' }}
              />
              <Typography>{navigationValue === 0 ? NO_PO : NO_DOCUMENTS}</Typography>
            </Grid>
          ) : (
            <Grid
              container
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                height: { xs: '72vh', xl: '76vh' }
              }}>
              {navigationValue === 0 ? (
                <Grid container spacing={2}>
                  {/* PO Card */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
                        borderRadius: 2,
                        height: '65vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                      <Box
                        sx={{
                          width: '100%'
                        }}>
                        <GeneratePoCard
                          textSize={{ xs: '8px', lg: '10px', xl: '12px' }}
                          height={{ xs: '47vh', md: '59vh', lg: '61vh', xl: '42vh' }}
                          parentHeight="100%"
                          cursor={smallScreen ? 'pointer' : 'auto'}
                          onClick={handleCardOpen}
                          documentContentMapped={filteredArray}
                          documentLineItemsMapped={documentLineItemsMapped}
                          lineItemsPerPage={lineItemsPerPage}
                          totalCardPages={totalCardPages}
                          show={true}
                          width="auto"
                          isVista={isVista}
                          title={PO_CARD_TITLE}
                        />
                        <Modal
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          open={cardOpen}
                          onClose={handleClose}>
                          <Grid
                            sx={{
                              height: { xs: '60vh', md: '67vh', xl: '50vh' },
                              display: 'flex',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              alignItems: 'center',
                              transform:
                                documentLineItemsMapped?.length > 10 ? 'scale(1.20)' : 'scale(1.3)',
                              cursor: 'pointer'
                            }}
                            onClick={handleClose}>
                            <GeneratePoCard
                              textSize={{ xs: '8px', lg: '10px', xl: '12px' }}
                              cursor="pointer"
                              height={{ xs: '47vh', md: '60vh', xl: '45vh' }}
                              onClick={handleClose}
                              documentContentMapped={filteredArray}
                              documentLineItemsMapped={documentLineItemsMapped}
                              lineItemsPerPage={lineItemsPerPage}
                              totalCardPages={totalCardPages}
                              width={{ xs: '65vw', md: '24vw', xl: '18vw' }}
                              show={true}
                              isVista={isVista}
                              title={PO_CARD_TITLE}
                            />
                          </Grid>
                        </Modal>
                      </Box>
                    </Box>
                  </Grid>

                  {/* The commitment's real position renders INSIDE this column,
                      above the per-line table whose Received/Remaining columns
                      Foundation cannot fill. It is passed down rather than
                      wrapped around, because PoLineitems is itself the third
                      `Grid item` of this row — wrapping it in a plain Box took
                      it out of the grid and collapsed the column. */}
                  <PoLineitems
                    supportindDocLineitems={supportindDocLineitems}
                    commitmentPosition={commitmentPosition}
                  />

                  {/* Invoice Image */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
                        borderRadius: 2,
                        height: '65vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto'
                      }}>
                      {images?.length > 0 && (
                        <img
                          className={`${selectedImage ? 'image-container enlarged' : ''}`}
                          src={images[index]}
                          alt={index}
                          style={{
                            cursor: 'pointer',
                            height: '100%',
                            objectFit: 'contain',
                            maxHeight: '100%',
                            maxWidth: '100%',
                            aspectRatio: 1
                          }}
                          onClick={handleImageClick}
                        />
                      )}
                      {images?.length > 1 && (
                        <PaginationButtons
                          disableFirstButton={index === 0}
                          disableSecondButton={index === images?.length - 1}
                          slideLeft={slideLeft}
                          slideRight={slideRight}
                          index={index}
                          images={images}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                // DT view
                <>
                  <Grid
                    item
                    xs={12}
                    md={5.5}
                    sx={{
                      padding: '1.5rem',
                      height: '70vh',
                      backgroundColor: Colors.Canvas_Left_BG,
                      borderRadius: '10px'
                    }}>
                    <Grid
                      container
                      item
                      xs={12}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: '1rem' }}>
                      {/* Left: Label Text */}
                      <Typography sx={{ color: Colors.TOURQUISE }}>
                        {dtSwitch === 'invoice' ? INVOICE : INVOICE_LINEITEMS}
                      </Typography>

                      {/* Right: Button */}
                      <Button
                        variant="Outlined"
                        sx={{
                          border: '1px solid',
                          borderColor: Colors.TOURQUISE,
                          height: '2rem',
                          textTransform: 'none',
                          color: Colors.WHITE,
                          borderRadius: '10px',
                          '&:hover': { backgroundColor: 'transparent' }
                        }}
                        onClick={dtSwitchHandler}>
                        <SwitchAccountOutlined
                          sx={{ color: Colors.DARKBLUE, fontSize: '1em', mr: '.3em' }}
                        />
                        <Typography sx={{ fontSize: '1em' }}>
                          {dtSwitch === 'invoice' ? SHOW_LINEITEMS : SHOW_INVOICE}
                        </Typography>
                      </Button>
                    </Grid>

                    {dtSwitch === 'invoice' ? (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          height: '90%',
                          overflowY: 'hidden',
                          overflowX: 'hidden'
                        }}>
                        <Box>
                          {images?.length > 0 && (
                            <img
                              className={`${selectedImage ? 'image-container enlarged' : ''}`}
                              src={images[index]}
                              alt={index}
                              style={{
                                cursor: 'pointer',
                                height: '100%',
                                objectFit: 'contain',
                                maxHeight: '100%',
                                maxWidth: '100%',
                                aspectRatio: 1
                              }}
                              onClick={handleImageClick}
                            />
                          )}
                        </Box>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          height: '93%',
                          overflowY: 'auto',
                          overflowX: 'hidden'
                        }}>
                        {isEmpty(supportindDocLineitems) ? (
                          <Grid
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              color: Colors.WHITE,
                              alignItems: 'center',
                              height: '100%'
                            }}>
                            <Typography>{NO_LINEITEMS}</Typography>
                          </Grid>
                        ) : (
                          <>
                            <Grid
                              container
                              className="dt-lineItems-header"
                              style={{ position: 'sticky', top: 0 }}>
                              <Grid item xs={6} md={9}>
                                <p style={divStyle}>
                                  <DescriptionOutlined
                                    sx={{
                                      color: Colors.TOURQUISE,
                                      fontSize: '1rem',
                                      marginRight: '.2rem'
                                    }}
                                  />
                                  {DESCRIPTION}
                                </p>
                              </Grid>
                              <Grid
                                item
                                xs={6}
                                md={3}
                                sx={{ display: 'flex', justifyContent: 'center' }}>
                                <p style={divStyle}>
                                  <ShoppingCart
                                    sx={{
                                      color: Colors.TOURQUISE,
                                      fontSize: '1rem',
                                      marginRight: '.2rem'
                                    }}
                                  />
                                  {QUANTITY}
                                </p>
                              </Grid>
                            </Grid>

                            {groupedItems?.map((group, parentIdx) => (
                              <Box key={parentIdx} className="dt-lineItems">
                                {/* Parent Row */}
                                <Grid container className="parent-line-item dt-lineItems-parent">
                                  <Grid item xs={6} md={9}>
                                    <Typography sx={{ color: Colors.WHITE, fontSize: '12px' }}>
                                      {group?.description}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={6}
                                    md={3}
                                    sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Typography sx={{ color: Colors.WHITE, fontSize: '12px' }}>
                                      {group?.totalQuantity}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                {/* Child Rows */}
                                <Box className="child-line-group" sx={{ position: 'relative' }}>
                                  {group?.children?.map((lineItem, childIdx) => {
                                    const descItem = lineItem?.find(
                                      (i) => i.name === 'Description'
                                    );
                                    const qtyItem = lineItem?.find((i) => i.name === 'Quantity');

                                    return (
                                      <Grid container key={childIdx} className="child-line-item">
                                        <Grid
                                          item
                                          xs={6}
                                          md={9}
                                          sx={{
                                            cursor: isValidPage(descItem?.page)
                                              ? 'pointer'
                                              : 'default'
                                          }}
                                          onClick={() => {
                                            if (isValidPage(descItem?.page)) {
                                              setDtIndex(descItem?.page);
                                              handleItemClick(
                                                'button_' +
                                                  descItem?.value +
                                                  '**%**' +
                                                  parentIdx +
                                                  childIdx
                                              );
                                            }
                                          }}>
                                          <p style={divStyle}>
                                            <Check
                                              sx={{
                                                fontSize: '1rem',
                                                color: descItem?.match
                                                  ? Colors.TOURQUISE
                                                  : Colors.WHITE,
                                                marginRight: '.5rem'
                                              }}
                                            />
                                            {descItem?.value || '-'}
                                          </p>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={6}
                                          md={3}
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            cursor: isValidPage(qtyItem?.page)
                                              ? 'pointer'
                                              : 'default'
                                          }}
                                          onClick={() => {
                                            if (isValidPage(qtyItem?.page)) {
                                              setDtIndex(qtyItem?.page);
                                              handleItemClick(
                                                'button_' +
                                                  qtyItem?.value +
                                                  '**%**' +
                                                  parentIdx +
                                                  childIdx
                                              );
                                            }
                                          }}>
                                          <p style={divStyle}>{qtyItem?.value || '-'}</p>
                                        </Grid>
                                      </Grid>
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
              <>
                {navigationValue !== 0 ? (
                  <>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{
                        p: '1rem',
                        height: '70vh',
                        display: 'flex',
                        backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
                        justifyContent: 'center',
                        borderRadius: '10px'
                      }}>
                      {dTUrl?.length > 0 && (
                        <SupportingDocumentWithBoundingBoxes
                          lineItemsArray={supportindDocLineitems}
                          dTUrl={dTUrl}
                          dtIndex={dtIndex}
                          setImageDimensions={setImageDimensions}
                          imageDimensions={imageDimensions}
                          selectedItemIndex={selectedItemIndex}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sx={{ mt: '5px' }}>
                      <Grid
                        container
                        spacing={2}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between' // Ensures two columns spaced apart
                        }}>
                        {/* Invoice Pagination – Left Column */}
                        <Grid item xs={12} md={5}>
                          <Box display="flex" justifyContent="center" width="100%">
                            {images?.length > 1 && dtSwitch === 'invoice' && (
                              <PaginationButtons
                                disableFirstButton={index === 0}
                                disableSecondButton={index === images?.length - 1}
                                slideLeft={slideLeft}
                                slideRight={slideRight}
                                index={index}
                                images={images}
                              />
                            )}
                          </Box>
                        </Grid>

                        {/* DT Pagination – Right Column */}
                        <Grid item xs={12} md={5}>
                          <Box display="flex" justifyContent="center" width="100%">
                            {dTUrl?.length > 1 && (
                              <PaginationButtons
                                disableFirstButton={dtIndex === 0}
                                disableSecondButton={dtIndex === dTUrl?.length - 1}
                                slideLeft={slideLeftDT}
                                slideRight={slideRightDT}
                                index={dtIndex}
                                images={dTUrl}
                              />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  ''
                )}
              </>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
}

export default SupportingDocument;
